// /app/frontend/src/pages/compliance/questionnaire/QuestionnaireResponsePage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Send, CheckCircle2, Loader2, ChevronRight,
  Shield, Users, Database, Lock, Building2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

import { useAuth } from '../../../contexts/AuthContext';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../../../components/ui/alert-dialog';

import { debounce } from 'lodash';
import { questionnaireAPI, tempGuestAPI } from '../../../services/api';

const AUTOSAVE_DELAY = 5000;

// Domain configuration with icons and colors
const DOMAIN_CONFIG = {
  GOV: { 
    name: 'Governance & Accountability', 
    description: 'Policies, procedures, and organizational measures for data protection compliance',
    icon: Shield,
    bgColor: 'bg-purple-100',
    iconColor: 'text-purple-600',
    progressColor: 'bg-purple-500'
  },
  RCT: { 
    name: 'Rights, Consent & Transparency', 
    description: 'Data subject rights, consent management, and privacy notices',
    icon: Users,
    bgColor: 'bg-pink-100',
    iconColor: 'text-pink-600',
    progressColor: 'bg-pink-500'
  },
  DLM: { 
    name: 'Data Lifecycle Management', 
    description: 'Data collection, storage, retention, and disposal practices',
    icon: Database,
    bgColor: 'bg-teal-100',
    iconColor: 'text-teal-600',
    progressColor: 'bg-teal-500'
  },
  SEC: { 
    name: 'Information Security', 
    description: 'Technical and organizational security measures to protect personal data',
    icon: Lock,
    bgColor: 'bg-yellow-100',
    iconColor: 'text-yellow-600',
    progressColor: 'bg-yellow-500'
  },
  TPM: { 
    name: 'Third Party Management', 
    description: 'Vendor management, data processing agreements, and transfers',
    icon: Building2,
    bgColor: 'bg-red-100',
    iconColor: 'text-red-600',
    progressColor: 'bg-red-500'
  },
  INC: { 
    name: 'Incident & Breach Management', 
    description: 'Breach detection, response, notification, and documentation',
    icon: AlertTriangle,
    bgColor: 'bg-orange-100',
    iconColor: 'text-orange-600',
    progressColor: 'bg-orange-500'
  }
};

// Answer options with numbers
const ANSWER_OPTIONS = [
  { key: 'we_do_this', label: 'We do this', description: 'Fully implemented and operating effectively', score: 5, number: 1 },
  { key: 'we_mostly_do_this', label: 'We mostly do this', description: 'Largely in place with minor gaps', score: 4, number: 2 },
  { key: 'we_partially_do_this', label: 'We partially do this', description: 'Some elements in place, significant gaps remain', score: 3, number: 3 },
  { key: 'we_plan_to_do_this', label: 'We plan to do this', description: 'Planned or in early stages but not yet operational', score: 2, number: 4 },
  { key: 'we_dont_do_this', label: "We don't do this", description: 'Not implemented and not currently planned', score: 1, number: 5 },
  { key: 'na', label: 'N/A', description: 'Not applicable to our organization', score: 0, number: 6 }
];

export default function QuestionnaireResponsePage({ guestMode = false }) {
  const { token } = useParams();
  const navigate = useNavigate();
  const { isComplianceReadOnly: _isReadOnly } = useAuth();
  const guestUser = guestMode ? JSON.parse(localStorage.getItem('guest_user') || '{}') : null;
  // Guests always have write access to their assigned form
  const isReadOnly = guestMode ? false : _isReadOnly;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [lastSaved, setLastSaved] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // View state: 'overview' | 'section' | 'question'
  const [view, setView] = useState('overview');
  const [currentSection, setCurrentSection] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);

  const autosaveTimerRef = useRef(null);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  // Stable refs so debounced save never captures a stale closure
  const guestModeRef = useRef(guestMode);
  const tokenRef = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // Load questionnaire
  useEffect(() => {
    loadQuestionnaire();
  }, [token]);

  // Cleanup autosave timer
  useEffect(() => {
    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, []);

  // Save to localStorage (token mode only — guest mode auto-saves to backend)
  useEffect(() => {
    if (!guestModeRef.current && Object.keys(answers).length > 0 && questionnaire) {
      localStorage.setItem(`questionnaire_draft_${token}`, JSON.stringify({
        answers,
        savedAt: new Date().toISOString()
      }));
    }
  }, [answers, token, questionnaire]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (view !== 'question' || !currentSection) return;
      
      const questions = groupedQuestions[currentSection] || [];
      const currentQuestion = questions[currentQuestionIndex];
      if (!currentQuestion) return;
      
      // Number keys 1-6 for answer selection
      if (e.key >= '1' && e.key <= '6') {
        const optionIndex = parseInt(e.key) - 1;
        if (optionIndex < ANSWER_OPTIONS.length) {
          handleAnswerChange(currentQuestion.question_id, ANSWER_OPTIONS[optionIndex]);
        }
      }

      // Enter for next question
      if (e.key === 'Enter' && answers[currentQuestion.question_id]) {
        if (currentQuestionIndex < questions.length - 1) {
          setCurrentQuestionIndex(prev => prev + 1);
        } else {
          setView('overview');
          setCurrentSection(null);
        }
      }
      
      // Escape to go back
      if (e.key === 'Escape') {
        setView('overview');
        setCurrentSection(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [view, currentSection, currentQuestionIndex, answers]);

  // Warn on page leave
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadQuestionnaire = async () => {
    try {
      setLoading(true);
      let response;
      if (guestModeRef.current) {
        response = await tempGuestAPI.getQuestionnaire();
      } else {
        response = await questionnaireAPI.getByToken(token);
      }
      setQuestionnaire(response.data);

      // Load existing responses from backend
      const existingAnswers = {};
      (response.data.responses || []).forEach(r => {
        existingAnswers[r.question_id] = {
          answer_key: r.answer_key,
          answer_label: r.answer_label,
          score_value: r.score_value
        };
      });

      // Merge with any localStorage draft (token mode only)
      const storageKey = guestModeRef.current ? null : `questionnaire_draft_${token}`;
      const stored = storageKey ? localStorage.getItem(storageKey) : null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setAnswers({ ...existingAnswers, ...parsed.answers });
        } catch {
          setAnswers(existingAnswers);
        }
      } else {
        setAnswers(existingAnswers);
      }
    } catch (error) {
      console.error('Error loading questionnaire:', error);
      if (error.response?.status === 410) {
        toast.error('This invitation link has expired');
      } else if (error.response?.status === 400) {
        toast.error('Invalid link or questionnaire already submitted');
      } else {
        toast.error('Failed to load questionnaire');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, option) => {
    const newAnswers = {
      ...answers,
      [questionId]: {
        answer_key: option.key,
        answer_label: option.label,
        score_value: option.score
      }
    };
    setAnswers(newAnswers);
    setHasUnsavedChanges(true);
    
    // Schedule autosave
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      handleSave(true);
    }, AUTOSAVE_DELAY);
  };

  const handleSave = async (isAutosave = false) => {
    if (!questionnaire || Object.keys(answersRef.current).length === 0) return;

    try {
      setSaving(true);

      const responses = Object.entries(answersRef.current).map(([questionId, answer]) => ({
        question_id: questionId,
        answer_key: answer.answer_key,
        answer_label: answer.answer_label,
        score_value: answer.score_value
      }));

      if (guestModeRef.current) {
        await tempGuestAPI.saveQuestionnaire({ responses });
      } else {
        await questionnaireAPI.saveByToken(tokenRef.current, responses, true);
      }

      setLastSaved(new Date());
      setHasUnsavedChanges(false);

      if (!isAutosave) {
        toast.success('Progress saved');
      }
    } catch (error) {
      console.error('Error saving:', error);
      if (!isAutosave) {
        toast.error('Failed to save progress');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      if (guestModeRef.current) {
        const responses = Object.entries(answersRef.current).map(([questionId, answer]) => ({
          question_id: questionId,
          answer_key: answer.answer_key,
          answer_label: answer.answer_label,
          score_value: answer.score_value
        }));
        const result = await tempGuestAPI.submitQuestionnaire({ responses });
        toast.success('Assessment submitted successfully!');
        setShowSubmitDialog(false);
        setQuestionnaire(prev => ({
          ...prev,
          submitted: true,
          score: result.data.score
        }));
        return;
      }
      await handleSave(false);
      const response = await questionnaireAPI.submitByToken(tokenRef.current);
      localStorage.removeItem(`questionnaire_draft_${tokenRef.current}`);
      toast.success('Assessment submitted successfully!');
      setShowSubmitDialog(false);
      setQuestionnaire(prev => ({
        ...prev,
        submitted: true,
        score: response.data.score
      }));
    } catch (error) {
      console.error('Error submitting:', error);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const openSection = (domainCode) => {
    setCurrentSection(domainCode);
    setCurrentQuestionIndex(0);
    setView('question');
  };

  // Group questions by domain
  const groupedQuestions = questionnaire?.questions?.reduce((acc, q) => {
    if (!acc[q.domain_code]) acc[q.domain_code] = [];
    acc[q.domain_code].push(q);
    return acc;
  }, {}) || {};

  // Calculate progress (only count questions that actually exist)
  const totalQuestions = questionnaire?.questions?.length || 0;
  const questionIds = new Set(questionnaire?.questions?.map(q => q.question_id) || []);
  const answeredQuestions = Array.from(questionIds).filter(qId => answers[qId]).length;
  const progressPercent = totalQuestions > 0 ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
  
  // Calculate section progress
  const getSectionProgress = (domainCode) => {
    const questions = groupedQuestions[domainCode] || [];
    const answered = questions.filter(q => answers[q.question_id]).length;
    return { answered, total: questions.length, percent: questions.length > 0 ? Math.round((answered / questions.length) * 100) : 0 };
  };

  // Count completed sections
  const completedSections = Object.entries(DOMAIN_CONFIG).filter(([code]) => {
    if (!groupedQuestions[code]) return false;
    const progress = getSectionProgress(code);
    return progress.answered === progress.total;
  }).length;

  const canSubmit = answeredQuestions === totalQuestions;

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading questionnaire...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (!questionnaire) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">Invalid Link</h2>
            <p className="text-muted-foreground">
              This questionnaire link is invalid, expired, or has already been submitted.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Submitted state
  if (questionnaire.submitted) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 px-6">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-2xl font-bold">Data Protection Assessment</h1>
            <p className="text-slate-300">{questionnaire.entity_name || questionnaire.campaign_name}</p>
          </div>
        </div>
        <div className="max-w-4xl mx-auto p-6">
          <Card className="text-center py-12">
            <CardContent>
              <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Assessment Submitted</h2>
              <p className="text-muted-foreground mb-6">Thank you for completing this assessment.</p>
              {questionnaire.score && (
                <div className="bg-muted rounded-lg p-6 inline-block">
                  <p className="text-sm text-muted-foreground mb-1">Your Score</p>
                  <p className="text-4xl font-bold text-primary">{questionnaire.score.overall_score?.toFixed(1)}%</p>
                  <p className="text-sm text-muted-foreground">{questionnaire.score.maturity_name}</p>
                </div>
              )}
              {guestModeRef.current && (
                <div className="mt-6">
                  <Button onClick={() => navigate('/guest/portal')}>Back to Portal</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Question View
  if (view === 'question' && currentSection) {
    const config = DOMAIN_CONFIG[currentSection];
    const questions = groupedQuestions[currentSection] || [];
    const currentQuestion = questions[currentQuestionIndex];
    const sectionProgress = getSectionProgress(currentSection);
    const Icon = config?.icon || Shield;

    return (
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-6 px-6">
          <div className="max-w-4xl mx-auto">
            <button 
              onClick={() => { setView('overview'); setCurrentSection(null); }}
              className="flex items-center gap-2 text-slate-300 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              All Sections
            </button>
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-lg ${config?.bgColor || 'bg-slate-100'}`}>
                <Icon className={`h-6 w-6 ${config?.iconColor || 'text-slate-600'}`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-xl font-semibold">{config?.name}</h1>
                  <Badge variant="secondary" className="bg-white/20 text-white">
                    {sectionProgress.percent}%
                  </Badge>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="text-white hover:bg-white/10"
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Progress bars */}
        <div className="bg-slate-800 pb-6 px-6">
          <div className="max-w-4xl mx-auto space-y-3">
            <div className="flex items-center gap-4">
              <div className={`h-2 flex-1 bg-slate-700 rounded-full overflow-hidden`}>
                <div 
                  className={`h-full ${config?.progressColor || 'bg-primary'} transition-all duration-300`}
                  style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}
                />
              </div>
              <span className="text-sm text-slate-400 w-12 text-right">
                {currentQuestionIndex + 1}/{questions.length}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-slate-400">Overall:</span>
              <div className="h-2 flex-1 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-slate-500 transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
              <span className="text-sm text-slate-400 w-12 text-right">{progressPercent}%</span>
            </div>
          </div>
        </div>

        {/* Question Card */}
        <div className="max-w-4xl mx-auto p-6">
          <Card className="bg-card border shadow-sm">
            <CardContent className="p-8">
              <div className="mb-6">
                <Badge variant="outline" className="mb-4">
                  {currentQuestion?.question_id?.replace('SAQ-', '').replace('-', '.')}
                </Badge>
                <h2 className="text-xl font-semibold">
                  {currentQuestion?.question_text}
                </h2>
              </div>

              {/* Answer Options - 2x2 grid for first 4, row for last 2 */}
              <div className="space-y-4 mt-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ANSWER_OPTIONS.slice(0, 4).map((option) => {
                    const isSelected = answers[currentQuestion?.question_id]?.answer_key === option.key;
                    return (
                      <button
                        key={option.key}
                        onClick={() => !isReadOnly && handleAnswerChange(currentQuestion.question_id, option)}
                        disabled={isReadOnly}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-card hover:border-muted-foreground/50 hover:shadow-sm'
                        } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            {option.number}
                          </span>
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {ANSWER_OPTIONS.slice(4).map((option) => {
                    const isSelected = answers[currentQuestion?.question_id]?.answer_key === option.key;
                    return (
                      <button
                        key={option.key}
                        onClick={() => !isReadOnly && handleAnswerChange(currentQuestion.question_id, option)}
                        disabled={isReadOnly}
                        className={`p-4 rounded-lg border-2 text-left transition-all ${
                          isSelected
                            ? 'border-primary bg-primary/10'
                            : 'border-border bg-card hover:border-muted-foreground/50 hover:shadow-sm'
                        } ${isReadOnly ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium ${
                            isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                          }`}>
                            {option.number}
                          </span>
                          <div>
                            <p className="font-medium">{option.label}</p>
                            <p className="text-sm text-muted-foreground mt-1">{option.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={() => {
                    if (currentQuestionIndex > 0) {
                      setCurrentQuestionIndex(prev => prev - 1);
                    } else {
                      setView('overview');
                      setCurrentSection(null);
                    }
                  }}
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {currentQuestionIndex > 0 ? 'Previous' : 'Back to Sections'}
                </Button>
                
                {currentQuestionIndex < questions.length - 1 ? (
                  <Button
                    onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                    disabled={!answers[currentQuestion?.question_id]}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={() => { setView('overview'); setCurrentSection(null); }}
                    variant="default"
                  >
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Complete Section
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Keyboard shortcuts */}
          <div className="fixed bottom-6 left-6 bg-card border rounded-lg p-4 text-sm shadow-lg">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-muted rounded text-xs">1-6</kbd>
                <span className="text-muted-foreground">Select response</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-muted rounded text-xs">Enter</kbd>
                <span className="text-muted-foreground">Next question</span>
              </div>
              <div className="flex items-center gap-2">
                <kbd className="px-2 py-1 bg-muted rounded text-xs">Esc</kbd>
                <span className="text-muted-foreground">Back to overview</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Overview View (default)
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 text-white py-8 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Data Protection Assessment</h1>
              <p className="text-slate-300 mt-1">{questionnaire.entity_name || questionnaire.campaign_name}</p>
            </div>
            {!isReadOnly && (
              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                disabled={saving || Object.keys(answers).length === 0}
                className="gap-2"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save Progress
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Overall Progress Card */}
        <Card className="shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-8">
              {/* Circular Progress */}
              <div className="relative w-32 h-32">
                <svg className="w-32 h-32 transform -rotate-90">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    className="stroke-muted"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    className="stroke-primary"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${2 * Math.PI * 56}`}
                    strokeDashoffset={`${2 * Math.PI * 56 * (1 - progressPercent / 100)}`}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 0.5s' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold text-primary">{progressPercent}%</span>
                  <span className="text-sm text-muted-foreground">{answeredQuestions}/{totalQuestions}</span>
                </div>
              </div>

              {/* Progress Info */}
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Overall Progress</h2>
                <p className="text-muted-foreground mb-4">
                  Complete all sections to submit your assessment. You can work on sections in any order.
                </p>
                <div className="flex gap-6 text-sm">
                  <div>
                    <span className="text-muted-foreground">{completedSections} of {Object.keys(groupedQuestions).length} sections complete</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">{answeredQuestions} of {totalQuestions} questions answered</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assessment Sections */}
        <div>
          <h2 className="text-lg font-semibold mb-4">Assessment Sections</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(DOMAIN_CONFIG).map(([code, config]) => {
              if (!groupedQuestions[code]) return null;
              
              const progress = getSectionProgress(code);
              const Icon = config.icon;
              const isComplete = progress.answered === progress.total;
              
              return (
                <Card 
                  key={code}
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => openSection(code)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${config.bgColor}`}>
                        <Icon className={`h-6 w-6 ${config.iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-semibold">{config.name}</h3>
                          <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        </div>
                        <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{config.description}</p>
                        <div className="mt-4">
                          <div className="flex items-center justify-between text-sm mb-2">
                            <span className="text-muted-foreground">{progress.answered} of {progress.total} questions</span>
                            <span className="text-muted-foreground">{progress.percent}%</span>
                          </div>
                          <div className="h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className={`h-full transition-all duration-300 ${isComplete ? 'bg-green-500' : config.progressColor}`}
                              style={{ width: `${progress.percent}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Ready to Submit */}
        <Card className="bg-gradient-to-r from-slate-800 to-slate-900 text-white border-0 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold mb-1">Ready to Submit?</h2>
                <p className="text-slate-300">
                  {canSubmit 
                    ? 'All questions answered. You can now submit your assessment.'
                    : `Complete all ${totalQuestions} questions to submit.`}
                </p>
              </div>
              {!isReadOnly && (
                <Button
                  size="lg"
                  disabled={!canSubmit}
                  onClick={() => setShowSubmitDialog(true)}
                  className="gap-2"
                >
                  <Send className="h-4 w-4" />
                  Submit Assessment
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Tip */}
        <div className="bg-muted rounded-lg px-4 py-3 text-sm inline-block">
          <span className="font-medium">Tip:</span> Click a section to start answering questions
        </div>

        {/* Last saved indicator */}
        {lastSaved && (
          <p className="text-sm text-muted-foreground text-center">
            Last saved: {lastSaved.toLocaleTimeString()}
          </p>
        )}
      </div>

      {/* Submit Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Submit Assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              Once submitted, you cannot modify your responses. Make sure you have reviewed all your answers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
